import { useState } from 'react'
import { useQueryParam } from 'use-query-params'

import { Spin } from '@pepper/ui'

import PercentChange from 'components/PercentChange'
import Graph from './Graph/Graph'
import StatBtn from './StatBtn/StatBtn'
import InsightWrapper from '../InsightWrapper'
import { DateRange, Stat, RoiStats } from '../types'

import { transformGraphData } from './utils'

import s from './StatGraph.module.css'

type Props = {
  period: DateRange
  data: RoiStats
  isLoading: boolean
}

function formatTime(seconds: number) {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(seconds % 60)

  return `${minutes} min ${remainingSeconds} s`
}

function formatDecimal(value: number) {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(value)
}

const StatGraph = ({ period, data, isLoading }: Props) => {
  const [selectedTab, setSelectedTab] = useState(1)
  const [showInsights] = useQueryParam('insights')

  if (isLoading)
    return (
      <div className={s.spinWrapper}>
        <Spin />
      </div>
    )

  const { traffic, rankingKeywords, visitors } = transformGraphData({
    period,
    stats: data,
  })

  const stats: Stat[] = [traffic, visitors, rankingKeywords]
  const selectedStats = stats.find(item => item.id === selectedTab)
  const { aggregate } = data
  const pageVisits = aggregate.page_visits
  const sessionDuration = aggregate.session_duration
  const sessionEngagement = aggregate.session_engagement_rate
  const insight = selectedStats?.insight

  return (
    <div className={s.wrapper}>
      <div className={s.btnWrapper}>
        {stats
          ? stats.map(item => (
              <StatBtn
                key={item.id}
                isActive={selectedTab === item.id}
                onClick={() => setSelectedTab(item.id)}
                {...item}
              />
            ))
          : null}
      </div>
      <div className={s.graphHeader}>
        <div className={s.metrics}>
          {pageVisits && (
            <div className={s.metric}>
              Pages per Visit
              <span className={s.value}>{formatDecimal(pageVisits.value)}</span>
              <PercentChange change={pageVisits.growth_percentage} />
            </div>
          )}
          <span className={s.separator}>•</span>
          {sessionDuration && (
            <div className={s.metric}>
              Session Duration
              <span className={s.value}>
                {formatTime(sessionDuration.value)}
              </span>
              <PercentChange change={sessionDuration.growth_percentage} />
            </div>
          )}
          <span className={s.separator}>•</span>
          {sessionEngagement && (
            <div className={s.metric}>
              Engagement Rate
              <span className={s.value}>
                {formatDecimal(sessionEngagement.value)}%
              </span>
              <PercentChange change={sessionEngagement.growth_percentage} />
            </div>
          )}
        </div>

        <div className={s.legend}>
          <div className={s.legend1}>
            <div className={s.dot} />
            {selectedStats?.title}
          </div>
          <div className={s.legend2}>
            <div className={s.dot} />
            {selectedStats?.progressTitle}
          </div>
        </div>
      </div>

      {stats && (
        <Graph
          dataPoints={selectedStats?.graphData}
          yAxisLabel={selectedStats?.yAxisLabel}
        />
      )}

      {showInsights === 'true' && insight && (
        <InsightWrapper
          cta={insight.cta}
          description={insight.description}
          redirectUrl={insight.action}
          impact={insight.impact}
        />
      )}
    </div>
  )
}

export default StatGraph
